import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCpu, FiClock, FiTrendingUp, FiActivity, FiBarChart2, FiFileText, FiSliders } from 'react-icons/fi';
import MonitoringAnalysisModal from './MonitoringAnalysisModal';
import DbmsPerfAnalysisModal from './DbmsPerfAnalysisModal';
import AwrReportPanel from './AwrReportPanel';
import './OraclePerfAnalysisModal.css';
import ConnTabsWindow, { ConnTabPane } from './ConnTabsWindow';
import memoWindow from '../common/memoWindow';
import { usePerfJump, takePerfHistory } from '../../utils/perfJump';
import OwnConnTarget from './OwnConnTarget';
import useOwnConnSession, { ORACLE_CONN } from '../../hooks/useOwnConnSession';

// 오라클 성능 분석 — 예전 "성능 분석"(SQL 단위)과 "DBMS 성능 분석"(인스턴스 단위)을 한 창으로 합친 것.
//
// 합친 이유: 둘 다 같은 Oracle 연결을 대상으로 같은 질문("지금 느린 이유")에 답하는데
// 창이 갈려 있어 연결을 두 번 고르고 창을 오가야 했다. 관심사는 다르지만 작업 흐름은 하나다.
//
// 화면은 합치되 **탭은 그대로 분리**한다 — 두 도구의 관심사(SQL 한 건 드릴다운 vs 인스턴스 전체 집계)가
// 다르므로 한 화면에 섞으면 오히려 읽기 어려워진다.
//
// 구현: 각 원본 모달을 embedded 모드로 본문만 빌려 쓴다. 본문 로직을 복제하지 않으므로
// 어느 쪽을 고쳐도 이 창과 원본 창이 함께 따라온다.
const getTabs = (t) => [
    // 순서(2026-10-14 사용자 요청): 인스턴스 전체(실시간 → 이력) | SQL 한 건(실행 중 → 수집 → 이력) | AWR.
    // 「지금 무슨 일이 벌어지나」 에서 시작해 SQL 로 좁혀 가는 흐름이다 — 실시간 성능에서 고른 것이 실행 중인 쿼리로 넘어간다
    { key: 'dbms-realtime',   group: 'dbms', child: 'realtime',    label: t('monitoring:opa.tab.realtime'),    icon: FiActivity },
    { key: 'dbms-history',    group: 'dbms', child: 'history',     label: t('monitoring:opa.tab.history'),      icon: FiBarChart2 },
    { key: 'sql-active',      group: 'sql',  child: 'active',      label: t('monitoring:opa.tab.running'), icon: FiCpu },
    { key: 'sql-collections', group: 'sql',  child: 'collections', label: t('monitoring:opa.tab.collections'),      icon: FiClock },
    { key: 'sql-history',     group: 'sql',  child: 'history',     label: t('monitoring:opa.tab.execHistory'),      icon: FiTrendingUp },
    // 락 대기는 메뉴의 별도 창(LockWaitModal)으로 옮겼다(2026-10-07) — 같은 본문을 그 창이 빌려 쓴다
    // AWR 리포트 — 스냅샷 구간 단위. 실시간(dbms)·SQL 한 건(sql)과 관심사가 달라 따로 묶는다
    { key: 'awr-report',      group: 'awr',  child: 'report',      label: t('monitoring:opa.tab.awr'),          icon: FiFileText },
    // 설정(2026-10-16) — 대기 ↔ 메트릭 · 대기 ↔ 파라미터 매핑과 공개·복제. 실시간·이력과 같은 본문(DbmsPerf)이 그린다(고르던 대기가 이어진다)
    { key: 'dbms-settings',   group: 'dbms', child: 'settings',    label: t('monitoring:opa.tab.settings'),     icon: FiSliders },
];

/** 연결 하나의 본문 — 창(아래 OraclePerfAnalysisModal)이 DB 연결마다 탭으로 하나씩 띄운다(ConnTabsWindow, 2026-10-14) */
const OraclePerfAnalysisModalBody = ({ isOpen: windowOpen = false, onClose, connections = [], onOpenInEditor, darkMode = false, defaultConnectionId, active = true, excludeConnIds = [], onConnectionChange }) => {
    const { t } = useTranslation();
    const [activeKey, setActiveKey] = useState('dbms-realtime');
    // 묶음마다 마지막으로 본 탭 · 한 번이라도 연 묶음(2026-10-22) — 다른 묶음의 탭으로 갔다 와도 본문이 그대로 있게(예전에는 내렸다 다시 그려
    // 성능 이력에서 고른 대기·구간·눈금이 처음으로 돌아갔다 — 신고). 숨긴 묶음은 마운트된 채 두고 자동 갱신만 멈춘다(paused)
    const [lastChild, setLastChild] = useState({ dbms: 'realtime', sql: 'active', awr: 'report' });
    const [visited, setVisited] = useState(['dbms']);

    // 연결과 **세션**을 이 컨테이너가 소유한다 — 탭을 옮겨도 같은 연결·같은 세션을 본다.
    // (예전에는 창이 둘이라 각자 고르게 되어 서로 다른 DB 를 보고 있는 사고가 가능했다)
    const mon = useOwnConnSession({
        isOpen: windowOpen, connections, defaultConnectionId, label: 'oracle-perf',
        filter: ORACLE_CONN,
    });
    // 연결이 정해지기 전에는 조회하지 않는다(2026-10-14) — 연결 없이 나간 첫 조회에 서버가 예시 데이터를 주어,
    // 창을 열면 예시가 먼저 그려지고 실제 값이 그 위를 덮었다. 고를 연결이 아예 없을 때만 그대로 연다(예시 켜기·오류 안내가 보이게)
    const isOpen = windowOpen && (!!mon.connId || mon.conns.length === 0);
    // 탭 이름이 이 연결을 따라가게 창에 알린다(탭 안에서 연결을 바꿀 수 있다)
    useEffect(() => { if (mon.connId && onConnectionChange) onConnectionChange(mon.connId); }, [mon.connId]); // eslint-disable-line react-hooks/exhaustive-deps
    const selectedConnId = mon.connId;

    // 실시간 성능에서 고른 Top N 항목 → 실행 중인 쿼리 탭의 조회 조건.
    // 두 탭을 잇는 유일한 상태라 컨테이너가 들고 있는다(탭 컴포넌트끼리 직접 알지 않는다).
    const [liveFilter, setLiveFilter] = useState(null);

    // 다른 창이 「실행 이력」 으로 보낸 SQL — 이 연결의 것이면 꺼내 실행 이력 탭에서 연다(utils/perfJump)
    const jump = usePerfJump();
    const [historyReq, setHistoryReq] = useState(null);
    useEffect(() => {
        if (!mon.connId) return;
        const req = takePerfHistory(mon.connId);
        if (!req) return;
        setActiveKey('sql-history');
        setHistoryReq({ sqlId: req.sqlId, seq: req.seq });
    }, [jump, mon.connId]);

    const handleSelectTop = (dimension, value, windowMin) => {
        setLiveFilter({ dimension, value, windowMin });
        setActiveKey('sql-active');
    };

    if (!isOpen) return null;

    const tabs = getTabs(t);
    const tab = tabs.find(x => x.key === activeKey) || tabs[0];
    if (lastChild[tab.group] !== tab.child) setLastChild(prev => ({ ...prev, [tab.group]: tab.child }));
    if (!visited.includes(tab.group)) setVisited(prev => [...prev, tab.group]);
    // 묶음 하나의 본문 칸 — 보이는 묶음은 레이아웃에 그대로 끼고(display: contents), 숨긴 묶음은 마운트된 채 감춘다
    const pane = (group, node) => (visited.includes(group) || tab.group === group
        ? <div key={group} style={{ display: tab.group === group ? 'contents' : 'none' }}>{node}</div> : null);

    return (
        <ConnTabPane active={active} toolbar={<OwnConnTarget hideConn
                conns={mon.conns.filter(c => c.id === mon.connId || !excludeConnIds.includes(c.id))} connId={mon.connId}
                onConnChange={(id) => { mon.setConnId(id); setLiveFilter(null); }}
                sessionId={mon.sessionId} session={mon.session}
                sessionError={mon.sessionError} creating={mon.creating}
                onPickSession={(sid, cid) => { mon.pickSession(sid, cid); setLiveFilter(null); }}
            />}>

                <div className="oraperf-tabs">
                    {tabs.map((tb, idx) => {
                        const Icon = tb.icon;
                        // 두 도구 사이에 구분선 — 탭 5개가 한 줄에 있으면 어디까지가 SQL 단위이고
                        // 어디부터가 인스턴스 단위인지 경계가 보이지 않는다.
                        const isGroupStart = idx > 0 && tabs[idx - 1].group !== tb.group;
                        return (
                            <React.Fragment key={tb.key}>
                                {isGroupStart && <div className="oraperf-tab-divider" />}
                                <button
                                    className={`oraperf-tab ${activeKey === tb.key ? 'active' : ''}`}
                                    onClick={() => setActiveKey(tb.key)}
                                >
                                    <Icon size={13} /> {tb.label}
                                </button>
                            </React.Fragment>
                        );
                    })}
                    <div className="oraperf-tabs-spacer" />
                    {/* 연결 선택은 제목줄(OwnConnTarget)로 옮겼다 — 다섯 창이 같은 자리다 */}
                </div>

                {/* 한 번 연 묶음은 마운트된 채 둔다(2026-10-22) — 내리면 화면 상태가 사라진다. 대신 숨긴 묶음은 자동 갱신을 멈춘다(paused) */}
                <div className="oraperf-body">
                    {pane('awr', (
                        <AwrReportPanel
                            selectedConnId={selectedConnId}
                            monParams={mon.params}
                            monRun={mon.run}
                        />
                    ))}
                    {pane('sql', (
                        <MonitoringAnalysisModal
                            embedded
                            isOpen={isOpen}
                            onClose={onClose}
                            connections={connections}
                            onOpenInEditor={onOpenInEditor}
                            darkMode={darkMode}
                            activeTab={lastChild.sql}
                            paused={tab.group !== 'sql'}
                            // 본문이 「수집 시작」 등으로 탭을 바꾸고 싶을 때 — 탭 소유자는 이쪽이다
                            onRequestTab={(child) => {
                                const next = tabs.find(x => x.group === 'sql' && x.child === child);
                                if (next) setActiveKey(next.key);
                            }}
                            selectedConnId={selectedConnId}
                            monParams={mon.params}
                            monRun={mon.run}
                            liveFilter={lastChild.sql === 'active' ? liveFilter : null}
                            historyRequest={historyReq}
                            onClearLiveFilter={() => setLiveFilter(null)}
                        />
                    ))}
                    {pane('dbms', (
                        <DbmsPerfAnalysisModal
                            embedded
                            isOpen={isOpen}
                            onClose={onClose}
                            connections={connections}
                            darkMode={darkMode}
                            activeTab={lastChild.dbms}
                            paused={tab.group !== 'dbms'}
                            selectedConnId={selectedConnId}
                            monParams={mon.params}
                            monRun={mon.run}
                            onSelectTop={handleSelectTop}
                            // 범례의 SQL ID → 실행 이력 탭에서 그 SQL(2026-10-27). 실행 이력은 historyRequest 의 seq 가 바뀔 때마다 연다
                            onOpenSqlHistory={(sqlId) => { setActiveKey('sql-history'); setHistoryReq({ sqlId, seq: Date.now() }); }}
                            onRequestTab={(child) => {
                                const next = tabs.find(x => x.group === 'dbms' && x.child === child);
                                if (next) setActiveKey(next.key);
                            }}
                        />
                    ))}
                </div>

                <div className="oraperf-footer">
                    <button className="app-btn oraperf-btn" onClick={onClose}>{t('monitoring:opa.close')}</button>
                </div>
        </ConnTabPane>
    );
};


/**
 * 창 — **DB 연결마다 탭**(2026-10-14 요청: 여러 DB 를 동시에 조회). 탭마다 위의 본문 하나(자기 연결의 전담 세션).
 */
const OraclePerfAnalysisModal = (props) => {
    const { t } = useTranslation();
    // 세션 분석 등에서 「실행 이력」 으로 보낸 요청 — 그 연결의 탭을 앞으로(본문이 실행 이력을 연다)
    const jump = usePerfJump();
    return (
    <ConnTabsWindow sessionLabel="oracle-perf" focus={jump || undefined}
        isOpen={props.isOpen} onClose={props.onClose} connections={props.connections}
        defaultConnectionId={props.defaultConnectionId} filter={ORACLE_CONN}
        icon={<FiBarChart2 size={14} />} title={t('monitoring:opa.title')} closeLabel={t('monitoring:opa.close')} overlayClassName="oraperf-overlay" className="oraperf-modal" headerClassName="oraperf-header" closeClassName="oraperf-close"
        renderBody={(tab) => <OraclePerfAnalysisModalBody {...props} {...tab} />}
    />
    );
};

// 작업 탭을 오갈 때 Header 가 새 콜백을 넘겨도 본문을 다시 그리지 않는다(common/memoWindow)
export default memoWindow(OraclePerfAnalysisModal);
